import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, Grid, MenuItem, TextField } from "@mui/material";
import { Navigate } from "react-router-dom";

import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";

const selectFieldProps = {
  InputLabelProps: { shrink: true },
  sx: {
    "& .MuiOutlinedInput-root": {
      minHeight: 56,
      alignItems: "center",
    },
  },
};

function BulkImportPreviousYearQuestions() {
  const [redirect, setRedirect] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    color: "success",
    message: "",
  });
  const [allExamLists, setAllExamLists] = useState([]);
  const [form, setForm] = useState({
    exam_list_id: "",
    questionsArray: "",
  });
  const [autoSetForm, setAutoSetForm] = useState({
    unlock_count: 0,
    questions_per_set: 20,
    time_per_question: 60,
    status: "inactive",
  });

  const showMessage = (message, color = "success", shouldRedirect = false) => {
    setNotification({ open: true, color, message });
    setTimeout(() => {
      setNotification({ open: false, color, message: "" });
      if (shouldRedirect) {
        setRedirect(true);
      }
    }, shouldRedirect ? 2500 : 4000);
  };

  const loadExamLists = async () => {
    if (init() !== "success") return;
    try {
      const resp = await axios.get(`${Constants.getUrls.getAllExamLists}?page=1&limit=1000`);
      setAllExamLists(resp?.data?.data?.docs || []);
    } catch (error) {
      showMessage("Unable to load exam lists.", "error");
    }
  };

  useEffect(() => {
    loadExamLists();
  }, []);

  const handleChange = (name, value) => {
    if (name === "exam_list_id") {
      setForm((prev) => ({
        ...prev,
        exam_list_id: value,
      }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAutoSetChange = (name, value) => {
    setAutoSetForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const resp = await axios.post(Constants.postUrls.createPreviousYearBulkImport, {
        ...form,
        questions_per_set: autoSetForm.questions_per_set,
        time_per_question: autoSetForm.time_per_question,
        unlock_count: autoSetForm.unlock_count,
        status: autoSetForm.status,
      });
      showMessage(
        resp?.data?.message || "Previous year questions imported successfully.",
        "success",
        true
      );
    } catch (error) {
      showMessage(
        error?.response?.data?.message || "Unable to bulk import previous year questions.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  if (redirect) return <Navigate to="/previous-year-questions" />;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
          >
            <MDTypography variant="h6" color="white">
              Bulk Import Previous Year Questions
            </MDTypography>
          </MDBox>

          <MDBox py={3} px={2}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Exam List"
                    value={form.exam_list_id}
                    onChange={(e) => handleChange("exam_list_id", e.target.value)}
                    {...selectFieldProps}
                  >
                    <MenuItem value="">Select Exam List</MenuItem>
                    {allExamLists.map((item) => (
                      <MenuItem key={item._id} value={item._id}>
                        {item?.exam_name || "Exam List"} {item?.shift ? `• ${item.shift}` : ""}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={2}>
                  <TextField
                    type="number"
                    fullWidth
                    label="Questions Per Set"
                    value={autoSetForm.questions_per_set}
                    onChange={(e) => handleAutoSetChange("questions_per_set", e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} md={2}>
                  <TextField
                    type="number"
                    fullWidth
                    label="Time Per Question (sec)"
                    value={autoSetForm.time_per_question}
                    onChange={(e) => handleAutoSetChange("time_per_question", e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} md={2}>
                  <TextField
                    type="number"
                    fullWidth
                    label="Unlock First Sets"
                    value={autoSetForm.unlock_count}
                    onChange={(e) => handleAutoSetChange("unlock_count", e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Set Status"
                    value={autoSetForm.status}
                    onChange={(e) => handleAutoSetChange("status", e.target.value)}
                    {...selectFieldProps}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                    <MenuItem value="active_for_admin">Active For Admin</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <MDTypography variant="caption" color="text" display="block" mb={1}>
                    Sets will be auto created during import itself. You only need to select the
                    exam list, define questions per set, time per question, unlock count, and then
                    paste the JSON.
                  </MDTypography>
                  <MDTypography variant="caption" color="text" display="block" mb={1}>
                    Paste a JSON array. Each item should contain question, option1, option2,
                    option3, option4 and answer. Optional keys: question_hindi, option*_hindi,
                    description, description_hindi, question_pattern, question_type, time,
                    subject_id and topic_id.
                  </MDTypography>
                  <TextField
                    multiline
                    minRows={12}
                    fullWidth
                    label="Question JSON Array"
                    value={form.questionsArray}
                    onChange={(e) => handleChange("questionsArray", e.target.value)}
                  />
                </Grid>
              </Grid>

              <Grid align="left" mt={2}>
                <MDButton variant="contained" size="small" color="info" type="submit">
                  {loading ? "Importing..." : "Import Questions"}
                </MDButton>
              </Grid>
            </form>
          </MDBox>

          <MDSnackbar
            color={notification.color}
            icon="notifications"
            title="AgriQuiz"
            content={notification.message}
            dateTime="Just now"
            open={notification.open}
          />
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

export default BulkImportPreviousYearQuestions;
